import React, { createContext, useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";

/**
 * The views panel sits between the icon rail and the page. Any page can fill it:
 *
 *   <ShellPanel title="Deals">…saved views…</ShellPanel>
 *
 * The panel only takes space while something is registered in it.
 */
interface ShellContextValue {
  panelEl: HTMLElement | null;
  setPanelEl: (el: HTMLElement | null) => void;
  panelCount: number;
  registerPanel: () => () => void;
  /** Slots in the top bar that a page fills with its title, view switch and main actions */
  headerEl: HTMLElement | null;
  setHeaderEl: (el: HTMLElement | null) => void;
  actionsEl: HTMLElement | null;
  setActionsEl: (el: HTMLElement | null) => void;
  headerCount: number;
  registerHeader: () => () => void;
  /** Name of the record on screen, shown as the last breadcrumb in place of "Account", "Deal", … */
  recordLabel: string | null;
  setRecordLabel: (label: string | null) => void;
  /** The person hid the views panel with the top bar's button (shown by default; remembered on this browser) */
  viewsHidden: boolean;
  setViewsHidden: (v: boolean) => void;
}

const ShellContext = createContext<ShellContextValue | null>(null);

export function ShellProvider({ children }: { children: React.ReactNode }) {
  const [panelEl, setPanelEl] = useState<HTMLElement | null>(null);
  const [panelCount, setPanelCount] = useState(0);
  const [headerEl, setHeaderEl] = useState<HTMLElement | null>(null);
  const [actionsEl, setActionsEl] = useState<HTMLElement | null>(null);
  const [headerCount, setHeaderCount] = useState(0);
  const [recordLabel, setRecordLabel] = useState<string | null>(null);
  const [viewsHidden, setViewsHiddenState] = useState(() => {
    try {
      return localStorage.getItem("shell.viewsHidden") === "1";
    } catch {
      return false;
    }
  });
  const setViewsHidden = React.useCallback((v: boolean) => {
    setViewsHiddenState(v);
    try {
      localStorage.setItem("shell.viewsHidden", v ? "1" : "0");
    } catch {
      /* not remembered; fine */
    }
  }, []);

  const registerHeader = React.useCallback(() => {
    setHeaderCount((n) => n + 1);
    return () => setHeaderCount((n) => Math.max(0, n - 1));
  }, []);

  const registerPanel = React.useCallback(() => {
    setPanelCount((n) => n + 1);
    return () => setPanelCount((n) => Math.max(0, n - 1));
  }, []);

  return <ShellContext.Provider value={{ panelEl, setPanelEl, panelCount, registerPanel, headerEl, setHeaderEl, actionsEl, setActionsEl, headerCount, registerHeader, recordLabel, setRecordLabel, viewsHidden, setViewsHidden }}>{children}</ShellContext.Provider>;
}

export function useShell() {
  const ctx = useContext(ShellContext);
  if (!ctx) throw new Error("useShell must be used inside <ShellProvider>");
  return ctx;
}

export function ShellPanel({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  const { panelEl, registerPanel } = useShell();
  useEffect(() => registerPanel(), [registerPanel]);
  if (!panelEl) return null;
  return createPortal(
    <div className="flex flex-col gap-0.5 px-2.5 py-4">
      <div className="px-2.5 pb-3">
        <h2 className="text-base font-semibold tracking-tight text-foreground">{title}</h2>
        {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
      </div>
      {children}
    </div>,
    panelEl,
  );
}

/**
 * The page's own header in the top bar: title, count and view switch on the left,
 * main actions on the right. Without it the top bar shows breadcrumbs.
 */
export function ShellHeader({ title, count, switcher, actions }: { title: string; count?: React.ReactNode; switcher?: React.ReactNode; actions?: React.ReactNode }) {
  const { headerEl, actionsEl, registerHeader } = useShell();
  useEffect(() => registerHeader(), [registerHeader]);
  return (
    <>
      {headerEl &&
        createPortal(
          <>
            <h1 className="truncate text-[14px] font-semibold">
              {title}
              {count !== undefined && <span className="ml-1.5 font-normal text-muted-foreground">· {count}</span>}
            </h1>
            {switcher}
          </>,
          headerEl,
        )}
      {actionsEl && actions && createPortal(<>{actions}</>, actionsEl)}
    </>
  );
}

/**
 * A record page calls this with the record's name (undefined while it loads) so the top bar's last
 * breadcrumb reads "Accounts › Sri Lakshmi Mills" instead of "Accounts › Account".
 */
export function useRecordCrumb(label?: string | null) {
  const { setRecordLabel } = useShell();
  useEffect(() => {
    setRecordLabel(label?.trim() || null);
    return () => setRecordLabel(null);
  }, [label, setRecordLabel]);
}
